import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  Modal,
  SegmentedControl,
  Select,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi, VoiceFeedback } from './voiceClient';

interface Props { storeId: string; }

type Bucket = 'open' | 'resolved' | 'all';

const ISSUE_LABEL: Record<string, string> = {
  accurate: 'Accurate',
  misheard: 'Misheard',
  'wrong-item': 'Wrong item',
  'wrong-table': 'Wrong table',
  'hallucinated-data': 'Made-up data',
  latency: 'Slow',
  'wrong-answer': 'Wrong answer',
  other: 'Other'
};

function ratingTone(r: number | null): 'danger' | 'warning' | 'success' | 'muted' {
  if (r == null) return 'muted';
  if (r <= 2) return 'danger';
  if (r === 3) return 'warning';
  return 'success';
}
function Pill({ children, tone = 'muted' as 'danger' | 'warning' | 'success' | 'muted' }: { children: React.ReactNode; tone?: 'danger' | 'warning' | 'success' | 'muted' }) {
  const bg = tone === 'danger' ? 'var(--bso-danger-subtle)'
    : tone === 'warning' ? 'var(--bso-warning-subtle)'
    : tone === 'success' ? 'var(--bso-success-subtle)'
    : 'var(--bso-bg-raised)';
  const fg = tone === 'danger' ? 'var(--bso-danger)'
    : tone === 'warning' ? 'var(--bso-warning)'
    : tone === 'success' ? 'var(--bso-success)'
    : 'var(--bso-text-secondary)';
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 'var(--bso-radius-full)', background: bg, color: fg, fontSize: 12, fontWeight: 500 }}>
      {children}
    </span>
  );
}

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [bucket, setBucket] = React.useState<Bucket>('open');
  const [issueFilter, setIssueFilter] = React.useState<string>('');
  const [rows, setRows] = React.useState<VoiceFeedback[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState<VoiceFeedback | null>(null);
  const [resolutionNote, setResolutionNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await voiceApi.listFeedback(getIdToken, storeId, 200);
      setRows(r.feedback);
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to load feedback', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [getIdToken, storeId, show]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const filtered = React.useMemo(() => rows.filter((f) => {
    if (bucket === 'open' && f.acknowledgedAt) return false;
    if (bucket === 'resolved' && !f.acknowledgedAt) return false;
    if (issueFilter && f.issue !== issueFilter) return false;
    return true;
  }), [rows, bucket, issueFilter]);

  const openCount = rows.filter((f) => !f.acknowledgedAt).length;
  const low = rows.filter((f) => (f.rating ?? 3) <= 2 && !f.acknowledgedAt).length;

  const columns: DataTableColumn<VoiceFeedback>[] = [
    {
      key: 'submittedAt',
      header: 'Submitted',
      accessor: (r) => new Date(r.submittedAt).toLocaleString(),
      sortable: true,
      compare: (a, b) => a.submittedAt.localeCompare(b.submittedAt)
    },
    {
      key: 'rating',
      header: 'Rating',
      accessor: (r) => r.rating != null ? <Pill tone={ratingTone(r.rating)}>{r.rating}/5</Pill> : '—',
      align: 'center',
      sortable: true,
      compare: (a, b) => (a.rating ?? 0) - (b.rating ?? 0)
    },
    {
      key: 'issue',
      header: 'Issue',
      accessor: (r) => r.issue ? (ISSUE_LABEL[r.issue] || r.issue) : '—'
    },
    {
      key: 'session',
      header: 'Session',
      accessor: (r) => r.sessionId ? (
        <code style={{ fontSize: 12, color: 'var(--bso-text-secondary)' }}>{r.sessionId.slice(0, 12)}…</code>
      ) : '—'
    },
    {
      key: 'notes',
      header: 'Notes',
      accessor: (r) => r.notes ? (
        <span style={{ color: 'var(--bso-text-secondary)', fontSize: 13 }} title={r.notes}>
          {r.notes.length > 60 ? r.notes.slice(0, 60) + '…' : r.notes}
        </span>
      ) : '—'
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (r) => r.acknowledgedAt
        ? <Pill tone="success">Resolved</Pill>
        : <Pill tone="warning">Open</Pill>
    },
    {
      key: 'actions',
      header: '',
      accessor: (r) => r.acknowledgedAt
        ? <span style={{ fontSize: 12, color: 'var(--bso-text-tertiary)' }}>{r.acknowledgedBy}</span>
        : <Button size="sm" variant="ghost" onClick={() => { setActive(r); setResolutionNote(''); }}>Resolve</Button>,
      align: 'right'
    }
  ];

  const handleResolve = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      await voiceApi.acknowledgeFeedback(getIdToken, storeId, active.feedbackKey, resolutionNote.trim() || undefined);
      show({ tone: 'success', title: 'Feedback resolved' });
      setActive(null);
      await refresh();
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to resolve', description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--bso-space-6)' }}>
        <div>
          <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Voice feedback</h1>
          <p style={{ color: 'var(--bso-text-secondary)', margin: 0 }}>
            {openCount} open • {low} low-rated waiting on review
          </p>
        </div>
        <Button variant="ghost" onClick={refresh}>Refresh</Button>
      </header>

      <div style={{ display: 'flex', gap: 'var(--bso-space-4)', marginBottom: 'var(--bso-space-4)', flexWrap: 'wrap' }}>
        <SegmentedControl<Bucket>
          aria-label="Filter"
          value={bucket}
          onChange={setBucket}
          options={[
            { value: 'open', label: `Open (${openCount})` },
            { value: 'resolved', label: 'Resolved' },
            { value: 'all', label: 'All' }
          ]}
        />
        <div style={{ minWidth: 220 }}>
          <Select
            aria-label="Filter by issue"
            value={issueFilter}
            onChange={(e) => setIssueFilter(e.target.value)}
            options={[{ value: '', label: 'All issues' }, ...Object.entries(ISSUE_LABEL).map(([v, l]) => ({ value: v, label: l }))]}
          />
        </div>
      </div>

      <Card>
        <Card.Body>
          <DataTable<VoiceFeedback>
            columns={columns}
            rows={filtered}
            getRowKey={(r) => r.feedbackKey}
            pageSize={25}
            emptyState={loading ? 'Loading…' : 'No feedback in this view.'}
          />
        </Card.Body>
      </Card>

      <Modal
        open={active !== null}
        onClose={() => setActive(null)}
        title="Resolve voice feedback"
        description={active ? `Session ${active.sessionId || '—'}, submitted ${new Date(active.submittedAt).toLocaleString()}` : undefined}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setActive(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleResolve} loading={submitting}>Mark resolved</Button>
          </>
        }
      >
        {active && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bso-space-3)' }}>
            <div>
              <strong>Rating:</strong> {active.rating ?? '—'}/5 · <strong>Issue:</strong> {active.issue ? (ISSUE_LABEL[active.issue] || active.issue) : '—'}
            </div>
            {active.notes && (
              <div style={{ padding: 'var(--bso-space-3)', background: 'var(--bso-bg-raised)', borderRadius: 'var(--bso-radius-md)' }}>
                <div style={{ fontSize: 12, color: 'var(--bso-text-tertiary)', marginBottom: 4 }}>Submitter notes</div>
                <div>{active.notes}</div>
              </div>
            )}
            <TextInput
              label="Resolution note (optional)"
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              placeholder="What did you do about it?"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export const VoiceFeedbackDashboard: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
